import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { BACKEND_NODE_URL } from '../config/constants';
import './AgentCreatorModal.css';

const AgentCreatorStepModal = ({ isOpen, onClose, companyId, userId, editingAgent, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Estados para ETAPA 6 (Base de Conhecimento)
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [uploadingUrl, setUploadingUrl] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Estados para ETAPA 8 (Formulário)
  const [novoNomeCampo, setNovoNomeCampo] = useState('');
  const [novaDescricaoCampo, setNovaDescricaoCampo] = useState('');
  
  // Estados para ETAPA 10 (Prompt Gerado)
  const [promptGerado, setPromptGerado] = useState('');
  const [gerandoPrompt, setGerandoPrompt] = useState(false);
  
  // Dados coletados
  const [formData, setFormData] = useState({
    // Step 1: Empresa
    empresa: null,
    empresaConfirmada: false,
    
    // Step 2: Infraestrutura
    conexao_id: null,
    projeto_id: null,
    conexoes: [],
    projetos: [],
    
    // Step 3: Dados básicos do agente
    nome: '',
    area_atuacao: '',
    
    // Step 4: Tipo
    tipo: '',
    
    // Step 5: Configurações
    temperatura: 0.7,
    funcao_principal: '',
    
    // ID do agente (salvo após ETAPA 5)
    agente_id: null,
    
    // Step 6: Base de conhecimento
    base_conhecimento: [],
    
    // Step 7: Catálogos
    catalogos_ids: [],
    catalogos_disponiveis: [],
    
    // Step 8: Formulário
    campos_formulario: []
  });

  const totalSteps = 10;

  // Carregar dados salvos do localStorage ao abrir
  useEffect(() => {
    if (isOpen && companyId) {
      const savedData = localStorage.getItem(`agent_creator_${companyId}`);
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          // Garantir que campos_formulario sempre seja um array
          if (!Array.isArray(parsed.campos_formulario)) {
            parsed.campos_formulario = [];
          }
          setFormData(parsed);
          setCurrentStep(parsed.lastStep || 1);
          console.log('[AgentCreator] Dados restaurados do localStorage:', parsed);
        } catch (err) {
          console.error('[AgentCreator] Erro ao restaurar dados:', err);
        }
      }
      
      // Carregar dados da empresa se ainda não tem
      if (!formData.empresa) {
        loadCompanyData();
      }
    }
  }, [isOpen, companyId]);

  // Salvar dados no localStorage sempre que formData mudar
  useEffect(() => {
    if (isOpen && companyId && formData.empresa) {
      const dataToSave = {
        ...formData,
        lastStep: currentStep,
        lastUpdate: new Date().toISOString()
      };
      localStorage.setItem(`agent_creator_${companyId}`, JSON.stringify(dataToSave));
      console.log('[AgentCreator] Dados salvos no localStorage');
    }
  }, [formData, currentStep, isOpen, companyId]);

  // Carregar dados do agente quando estiver editando
  useEffect(() => {
    if (isOpen && editingAgent) {
      loadAgentData(editingAgent.id);
    }
  }, [isOpen, editingAgent]);

  // Carregar infraestrutura ao entrar na ETAPA 2
  useEffect(() => {
    if (isOpen && currentStep === 2 && formData.conexoes.length === 0) {
      loadInfrastructure();
    }
  }, [currentStep, isOpen]);

  // Carregar catálogos ao entrar na ETAPA 7
  useEffect(() => {
    if (isOpen && currentStep === 7 && formData.catalogos_disponiveis.length === 0) {
      loadCatalogs();
    }
  }, [currentStep, isOpen]);

  const loadAgentData = async (agentId) => {
    try {
      setIsLoading(true);
      console.log('[AgentCreator] Carregando dados do agente:', agentId);
      
      const response = await fetch(`${BACKEND_NODE_URL}/api/agents/creator/load-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId, agentId })
      });

      const data = await response.json();
      if (data.success) {
        console.log('[AgentCreator] Dados do agente carregados:', data);
        setFormData(data.agentData);
        setPromptGerado(data.promptData || {});
      } else {
        throw new Error(data.error || 'Erro ao carregar agente');
      }
    } catch (err) {
      console.error('[AgentCreator] Erro ao carregar agente:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCompanyData = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${BACKEND_NODE_URL}/api/agents/creator/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId })
      });

      const data = await response.json();
      if (data.success) {
        setFormData(prev => ({
          ...prev,
          empresa: data.companyData
        }));
      }
    } catch (err) {
      setError('Erro ao carregar dados da empresa');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = async () => {
    // Validações por etapa
    if (currentStep === 1) {
      if (!formData.empresa?.nome_fantasia?.trim()) {
        setError('Nome Fantasia é obrigatório');
        return;
      }
      if (!formData.empresa?.segmento?.trim()) {
        setError('Segmento é obrigatório');
        return;
      }
      // Dados validados, marcar como confirmada
      updateFormData('empresaConfirmada', true);
    }
    
    // Etapa 2 não precisa de validação (campos opcionais)
    
    if (currentStep === 3) {
      if (!formData.nome.trim()) {
        setError('Digite o nome do agente');
        return;
      }
      if (!formData.area_atuacao) {
        setError('Selecione a área de atuação');
        return;
      }
    }
    
    if (currentStep === 4 && !formData.tipo) {
      setError('Selecione o tipo do agente');
      return;
    }
    
    if (currentStep === 5) {
      if (!formData.funcao_principal.trim()) {
        setError('Digite a função principal do agente');
        return;
      }
      // Salvar agente inicial antes de avançar para ETAPA 6
      await saveInitialAgent();
      if (error) return; // Se houver erro no salvamento, não avança
    }
    
    // Etapa 7 não precisa de validação (catálogos são opcionais)
    
    if (currentStep === 9) {
      // Gerar prompt com IA antes de avançar para ETAPA 10
      try {
        await gerarPromptComIA();
        // Só avança se não houver erro
      } catch (err) {
        // Erro já foi tratado em gerarPromptComIA
        return; // Não avança
      }
    }
    
    if (currentStep === 10) {
      // Última etapa: salvar tudo
      await finalizeAgent();
      return;
    }
    
    setError(null);
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setError(null);
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const loadInfrastructure = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('[AgentCreator] Buscando conexões e projetos para empresa:', companyId);
      
      // Usar endpoint do agentConfigurator que já busca corretamente
      const response = await fetch(`${BACKEND_NODE_URL}/api/agents/creator/infrastructure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId })
      });
      
      if (!response.ok) {
        throw new Error('Erro ao buscar infraestrutura');
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Erro ao buscar dados');
      }
      
      console.log('[AgentCreator] Conexões encontradas:', data.connections?.length || 0);
      console.log('[AgentCreator] Projetos encontrados:', data.projects?.length || 0);
      
      updateFormData('conexoes', data.connections || []);
      updateFormData('projetos', data.projects || []);
      
    } catch (err) {
      console.error('[AgentCreator] Erro ao carregar infraestrutura:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const saveInitialAgent = async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('[AgentCreator] Salvando agente inicial...');

      const response = await fetch(`${BACKEND_NODE_URL}/api/agents/creator/save-initial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId,
          agentData: {
            nome: formData.nome,
            area_atuacao: formData.area_atuacao,
            tipo: formData.tipo,
            temperatura: formData.temperatura,
            funcao_principal: formData.funcao_principal,
            conexao_id: formData.conexao_id,
            projeto_id: formData.projeto_id
          }
        })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Erro ao salvar agente');
      }

      console.log('[AgentCreator] Agente salvo com ID:', data.agente_id);

      // Salvar agente_id no formData
      updateFormData('agente_id', data.agente_id);

    } catch (err) {
      console.error('[AgentCreator] Erro ao salvar agente inicial:', err);
      setError(err.message);
      throw err; // Propagar erro para impedir avanço
    } finally {
      setIsLoading(false);
    }
  };

  const loadCatalogs = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('[AgentCreator] Buscando catálogos...');
      
      const response = await fetch(`${BACKEND_NODE_URL}/api/agents/creator/catalogs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId })
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Erro ao buscar catálogos');
      }
      
      console.log('[AgentCreator] Catálogos encontrados:', data.catalogs?.length || 0);
      
      updateFormData('catalogos_disponiveis', data.catalogs || []);
      
    } catch (err) {
      console.error('[AgentCreator] Erro ao carregar catálogos:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const gerarPromptComIA = async () => {
    try {
      setGerandoPrompt(true);
      setError(null);

      console.log('[AgentCreator] Gerando prompt com IA...');

      // Preparar dados COMPLETOS para o gerador
      const empresa = {
        nome_fantasia: formData.empresa?.nome_fantasia || '',
        segmento: formData.empresa?.segmento || '',
        subsegmento: formData.empresa?.subsegmento || '',
        descricao: formData.empresa?.descricao || '',
        telefone_principal: formData.empresa?.telefone_principal || '',
        cidade: formData.empresa?.cidade || ''
      };

      const agente = {
        nome: formData.nome,
        canal: 'WhatsApp',
        funcaoPrincipal: formData.funcao_principal,
        subFuncao: formData.tipo,
        descricaoFuncao: `Agente de ${formData.area_atuacao}`
      };

      // Preparar informações das ferramentas
      const baseConhecimento = {
        habilitado: formData.base_conhecimento.length > 0,
        documentos: formData.base_conhecimento.length
      };

      const catalogos = formData.catalogos_disponiveis
        .filter(cat => formData.catalogos_ids.includes(cat.id))
        .map(cat => ({
          nome: cat.nome,
          descricao: cat.descricao,
          total_produtos: cat.total_produtos
        }));

      const formulario = {
        campos: (formData.campos_formulario || []).map(campo => ({
          nome: campo.campo_nome,
          descricao: campo.campo_descricao,
          obrigatorio: campo.campo_obrigatorio
        }))
      };

      const config = {
        empresa,
        agente,
        baseConhecimento,
        catalogos,
        formulario
      };

      console.log('[AgentCreator] Dados a enviar:', { empresa, agente });
      console.log('[AgentCreator] URL:', `${BACKEND_NODE_URL}/api/agents/creator/generate-prompt`);

      // Timeout de 4 minutos (240 segundos)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 240000);

      const response = await fetch(`${BACKEND_NODE_URL}/api/agents/creator/generate-prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      console.log('[AgentCreator] Response status:', response.status);
      
      if (!response.ok) {
        const text = await response.text();
        console.error('[AgentCreator] Erro na resposta:', text);
        throw new Error(`Erro no servidor (${response.status}): ${text.substring(0, 100)}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('[AgentCreator] Resposta não é JSON:', text);
        throw new Error('Servidor retornou resposta inválida. Verifique se o backend está rodando corretamente.');
      }

      const data = await response.json();
      console.log('[AgentCreator] Response data:', data);

      if (!data.success) {
        throw new Error(data.error || 'Erro ao gerar prompt');
      }

      console.log('[AgentCreator] Prompt gerado com sucesso:', data.prompt);
      
      // Salvar prompt gerado no estado
      setPromptGerado(data.prompt);

    } catch (err) {
      console.error('[AgentCreator] Erro ao gerar prompt:', err);
      setError(err.message);
      throw err; // Propagar erro para impedir avanço
    } finally {
      setGerandoPrompt(false);
    }
  };

  const finalizeAgent = async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('[AgentCreator] Finalizando agente...');
      console.log('[AgentCreator] Prompt gerado:', promptGerado);
      console.log('[AgentCreator] Agent ID:', formData.agente_id);

      if (!formData.agente_id) {
        throw new Error('ID do agente não encontrado. Por favor, volte e salve o agente novamente.');
      }

      if (!promptGerado || Object.keys(promptGerado).length === 0) {
        throw new Error('Prompt não foi gerado. Por favor, volte para a etapa anterior.');
      }

      // Finalizar agente com todas as configurações
      const response = await fetch(`${BACKEND_NODE_URL}/api/agents/creator/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: companyId,
          agentId: formData.agente_id,
          promptGerado: promptGerado,
          connectionId: formData.conexao_id || null,
          projectId: formData.projeto_id || null,
          catalogosIds: formData.catalogos_ids || []
        })
      });

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Erro ao finalizar agente');
      }

      console.log('[AgentCreator] ✅ Agente finalizado com sucesso!');

      // Sucesso! Limpar dados salvos
      localStorage.removeItem(`agent_creator_${companyId}`);
      console.log('[AgentCreator] Dados do localStorage limpos');
      
      // Redirecionar para página do agente ou fechar modal
      if (onComplete) {
        onComplete(data.agentId);
      }
      
      alert('✅ Agente criado com sucesso!');
      onClose();
      
    } catch (err) {
      console.error('[AgentCreator] Erro ao finalizar:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="agent-creator-modal-overlay">
      <div className="agent-creator-modal" style={{ maxWidth: '800px' }}>
        {/* Header */}
        <div className="agent-creator-header">
          <div className="agent-creator-title">
            <div className="agent-creator-icon">🤖</div>
            <div>
              <h2>Criar Novo Agente</h2>
              <p>Etapa {currentStep} de {totalSteps}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              onClick={() => {
                if (window.confirm('Deseja limpar todos os dados e recomeçar?')) {
                  localStorage.removeItem(`agent_creator_${companyId}`);
                  window.location.reload();
                }
              }}
              style={{
                padding: '0.5rem 1rem',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '6px',
                color: '#fca5a5',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
              title="Limpar dados e recomeçar"
            >
              🔄 Recomeçar
            </button>
            <button onClick={onClose} className="agent-creator-close">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div style={{ padding: '0 2rem', marginBottom: '1rem' }}>
          <div style={{
            width: '100%',
            height: '4px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${(currentStep / totalSteps) * 100}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #b43bea, #7c3aed)',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        {/* Content */}
        <div style={{ 
          padding: '0 2rem 1rem 2rem', 
          minHeight: '400px',
          maxHeight: 'calc(70vh - 250px)',
          overflowY: 'auto',
          overflowX: 'hidden'
        }}>
          {renderStep()}
        </div>

        {/* Error */}
        {error && (
          <div style={{
            margin: '0 2rem 1rem 2rem',
            padding: '1rem',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            color: '#fca5a5'
          }}>
            ⚠️ {error}
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '1rem 2rem',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          gap: '1rem',
          background: 'linear-gradient(135deg, rgba(15, 12, 41, 0.98) 0%, rgba(25, 20, 60, 0.98) 100%)',
          position: 'sticky',
          bottom: 0,
          zIndex: 10
        }}>
          <button
            onClick={handleBack}
            disabled={currentStep === 1 || isLoading}
            style={{
              padding: '0.75rem 1.5rem',
              border: '1px solid rgba(180, 59, 234, 0.3)',
              borderRadius: '8px',
              background: 'rgba(255,255,255,0.05)',
              color: '#fff',
              cursor: currentStep === 1 || isLoading ? 'not-allowed' : 'pointer',
              opacity: currentStep === 1 || isLoading ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: 500
            }}
          >
            <ChevronLeft size={20} />
            Voltar
          </button>

          <button
            onClick={handleNext}
            disabled={isLoading || gerandoPrompt}
            style={{
              padding: '0.75rem 1.5rem',
              border: '1px solid rgba(180, 59, 234, 0.5)',
              borderRadius: '8px',
              background: 'rgba(180, 59, 234, 0.3)',
              color: '#fff',
              cursor: (isLoading || gerandoPrompt) ? 'not-allowed' : 'pointer',
              opacity: (isLoading || gerandoPrompt) ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: 500
            }}
          >
            {(isLoading || gerandoPrompt) ? 'Processando...' : currentStep === totalSteps ? (
              <>
                <Check size={20} />
                Finalizar
              </>
            ) : currentStep === 1 ? (
              <>
                Salvar e Prosseguir
                <ChevronRight size={20} />
              </>
            ) : (
              <>
                Próximo
                <ChevronRight size={20} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  function renderStep() {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      case 6:
        return renderStep6();
      case 7:
        return renderStep7();
      case 8:
        return renderStep8();
      case 9:
        return renderStep9();
      case 10:
        return renderStep10();
      default:
        return null;
    }
  }

  // STEP 1: Dados da empresa (editável)
  function renderStep1() {
    if (!formData.empresa) {
      return (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="spinner" style={{ margin: '0 auto 1rem auto' }}></div>
          <p>Carregando dados da empresa...</p>
        </div>
      );
    }

    const inputStyle = {
      width: '100%',
      padding: '0.75rem',
      background: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '8px',
      color: '#fff',
      fontSize: '1rem',
      marginBottom: '1rem'
    };

    const labelStyle = {
      display: 'block',
      marginBottom: '0.5rem',
      fontWeight: 500,
      color: 'rgba(255,255,255,0.9)'
    };

    return (
      <div>
        <h3 style={{ marginBottom: '0.5rem', fontSize: '1.5rem' }}>📌 Dados da Empresa</h3>
        <p style={{ marginBottom: '2rem', opacity: 0.7, fontSize: '0.95rem' }}>
          Revise e edite os dados da sua empresa se necessário
        </p>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          {/* Nome Fantasia */}
          <div>
            <label style={labelStyle}>Nome Fantasia *</label>
            <input
              type="text"
              value={formData.empresa.nome_fantasia || ''}
              onChange={(e) => updateFormData('empresa', { ...formData.empresa, nome_fantasia: e.target.value })}
              placeholder="Nome da empresa"
              style={inputStyle}
            />
          </div>

          {/* CNPJ */}
          <div>
            <label style={labelStyle}>CNPJ</label>
            <input
              type="text"
              value={formData.empresa.cnpj || ''}
              onChange={(e) => updateFormData('empresa', { ...formData.empresa, cnpj: e.target.value })}
              placeholder="00.000.000/0000-00"
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          {/* Segmento */}
          <div>
            <label style={labelStyle}>Segmento *</label>
            <input
              type="text"
              value={formData.empresa.segmento || ''}
              onChange={(e) => updateFormData('empresa', { ...formData.empresa, segmento: e.target.value })}
              placeholder="Ex: Alimentação"
              style={inputStyle}
            />
          </div>

          {/* Subsegmento */}
          <div>
            <label style={labelStyle}>Subsegmento</label>
            <input
              type="text"
              value={formData.empresa.subsegmento || ''}
              onChange={(e) => updateFormData('empresa', { ...formData.empresa, subsegmento: e.target.value })}
              placeholder="Ex: Pizzaria"
              style={inputStyle}
            />
          </div>
        </div>

        {/* Descrição */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Descrição da Empresa</label>
          <textarea
            value={formData.empresa.descricao || ''}
            onChange={(e) => updateFormData('empresa', { ...formData.empresa, descricao: e.target.value })}
            placeholder="Descreva brevemente o que sua empresa faz..."
            rows={3}
            style={{
              ...inputStyle,
              resize: 'vertical'
            }}
          />
        </div>

        {/* Endereço */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Endereço Completo</label>
          <input
            type="text"
            value={formData.empresa.logradouro || ''}
            onChange={(e) => updateFormData('empresa', { ...formData.empresa, logradouro: e.target.value })}
            placeholder="Rua, Avenida..."
            style={inputStyle}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          {/* Número */}
          <div>
            <label style={labelStyle}>Número</label>
            <input
              type="text"
              value={formData.empresa.numero || ''}
              onChange={(e) => updateFormData('empresa', { ...formData.empresa, numero: e.target.value })}
              placeholder="123"
              style={inputStyle}
            />
          </div>

          {/* Bairro */}
          <div>
            <label style={labelStyle}>Bairro</label>
            <input
              type="text"
              value={formData.empresa.bairro || ''}
              onChange={(e) => updateFormData('empresa', { ...formData.empresa, bairro: e.target.value })}
              placeholder="Centro"
              style={inputStyle}
            />
          </div>

          {/* CEP */}
          <div>
            <label style={labelStyle}>CEP</label>
            <input
              type="text"
              value={formData.empresa.cep || ''}
              onChange={(e) => updateFormData('empresa', { ...formData.empresa, cep: e.target.value })}
              placeholder="00000-000"
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          {/* Cidade */}
          <div>
            <label style={labelStyle}>Cidade</label>
            <input
              type="text"
              value={formData.empresa.cidade || ''}
              onChange={(e) => updateFormData('empresa', { ...formData.empresa, cidade: e.target.value })}
              placeholder="São Paulo"
              style={inputStyle}
            />
          </div>

          {/* Estado */}
          <div>
            <label style={labelStyle}>Estado</label>
            <input
              type="text"
              value={formData.empresa.estado || ''}
              onChange={(e) => updateFormData('empresa', { ...formData.empresa, estado: e.target.value })}
              placeholder="SP"
              maxLength={2}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Contatos */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          {/* Telefone Principal */}
          <div>
            <label style={labelStyle}>Telefone Principal</label>
            <input
              type="text"
              value={formData.empresa.telefone_principal || ''}
              onChange={(e) => updateFormData('empresa', { ...formData.empresa, telefone_principal: e.target.value })}
              placeholder="(11) 99999-9999"
              style={inputStyle}
            />
          </div>

          {/* Email */}
          <div>
            <label style={labelStyle}>Email de Contato</label>
            <input
              type="email"
              value={formData.empresa.email_contato || ''}
              onChange={(e) => updateFormData('empresa', { ...formData.empresa, email_contato: e.target.value })}
              placeholder="contato@empresa.com"
              style={inputStyle}
            />
          </div>
        </div>

        {/* Site */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Site</label>
          <input
            type="url"
            value={formData.empresa.site || ''}
            onChange={(e) => updateFormData('empresa', { ...formData.empresa, site: e.target.value })}
            placeholder="https://www.empresa.com.br"
            style={inputStyle}
          />
        </div>

      </div>
    );
  }

  // STEP 2: Conexões e Projetos
  function renderStep2() {
    if (isLoading && formData.conexoes.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="spinner" style={{ margin: '0 auto 1rem auto' }}></div>
          <p>Carregando conexões e projetos...</p>
        </div>
      );
    }

    return (
      <div>
        <h3 style={{ marginBottom: '0.5rem', fontSize: '1.5rem' }}>📱 Infraestrutura</h3>
        <p style={{ marginBottom: '2rem', opacity: 0.7, fontSize: '0.95rem' }}>
          Vincule uma conexão WhatsApp e/ou um projeto CRM (opcional)
        </p>
        
        {/* Conexões WhatsApp */}
        <div style={{ marginBottom: '2rem' }}>
          <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 500, fontSize: '1.1rem' }}>
            📱 Conexão WhatsApp
          </label>
          
          {formData.conexoes.length === 0 ? (
            <div style={{
              padding: '1.5rem',
              background: 'rgba(255, 193, 7, 0.1)',
              border: '1px solid rgba(255, 193, 7, 0.3)',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <p style={{ margin: 0, color: '#ffd54f' }}>
                ⚠️ Nenhuma conexão WhatsApp ativa encontrada.
              </p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.8 }}>
                Configure uma conexão em: Menu → Conexões
              </p>
            </div>
          ) : (
            <select
              value={formData.conexao_id || ''}
              onChange={(e) => updateFormData('conexao_id', e.target.value || null)}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem',
                marginBottom: '0.5rem'
              }}
            >
              <option value="" style={{ background: '#1a1a2e', color: '#fff' }}>Nenhuma (configurar depois)</option>
              {formData.conexoes.map(conn => (
                <option key={conn.id} value={conn.id} style={{ background: '#1a1a2e', color: '#fff' }}>
                  {conn.name} {conn.with_ai ? '(Com IA)' : ''}
                </option>
              ))}
            </select>
          )}
          
          <p style={{ fontSize: '0.85rem', opacity: 0.6, margin: '0.5rem 0 0 0' }}>
            {formData.conexoes.length} conexão(ões) WhatsApp ativa(s)
          </p>
        </div>

        {/* Projetos CRM */}
        <div>
          <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 500, fontSize: '1.1rem' }}>
            📊 Projeto CRM
          </label>
          
          {formData.projetos.length === 0 ? (
            <div style={{
              padding: '1.5rem',
              background: 'rgba(255, 193, 7, 0.1)',
              border: '1px solid rgba(255, 193, 7, 0.3)',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <p style={{ margin: 0, color: '#ffd54f' }}>
                ⚠️ Nenhum projeto CRM encontrado.
              </p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.8 }}>
                Crie um projeto em: Menu → Projetos
              </p>
            </div>
          ) : (
            <select
              value={formData.projeto_id || ''}
              onChange={(e) => updateFormData('projeto_id', e.target.value || null)}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem',
                marginBottom: '0.5rem'
              }}
            >
              <option value="" style={{ background: '#1a1a2e', color: '#fff' }}>Nenhum (configurar depois)</option>
              {formData.projetos.map(proj => (
                <option key={proj.id} value={proj.id} style={{ background: '#1a1a2e', color: '#fff' }}>
                  {proj.name}
                </option>
              ))}
            </select>
          )}
          
          <p style={{ fontSize: '0.85rem', opacity: 0.6, margin: '0.5rem 0 0 0' }}>
            {formData.projetos.length} projeto(s) CRM disponível(is)
          </p>
        </div>

        <div style={{
          padding: '1rem',
          background: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '8px',
          marginTop: '2rem'
        }}>
          <p style={{ margin: 0, color: '#93c5fd', fontSize: '0.9rem' }}>
            💡 Você pode vincular o agente a uma conexão e/ou projeto agora, ou configurar depois nas configurações do agente.
          </p>
        </div>
      </div>
    );
  }

  // STEP 3: Nome e Área
  function renderStep3() {
    return (
      <div>
        <h3 style={{ marginBottom: '1.5rem', fontSize: '1.5rem' }}>🤖 Dados do Agente</h3>
        
        <div style={{ marginBottom: '2rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
            Nome do Agente *
          </label>
          <input
            type="text"
            value={formData.nome}
            onChange={(e) => updateFormData('nome', e.target.value)}
            placeholder="Ex: João, Maria, Assistente Virtual"
            style={{
              width: '100%',
              padding: '0.75rem',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '1rem'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
            Área de Atuação *
          </label>
          <select
            value={formData.area_atuacao}
            onChange={(e) => updateFormData('area_atuacao', e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '1rem'
            }}
          >
            <option value="" style={{ background: '#1a1a2e', color: '#fff' }}>Selecione...</option>
            <option value="atendimento" style={{ background: '#1a1a2e', color: '#fff' }}>Atendimento ao Cliente</option>
            <option value="gestao" style={{ background: '#1a1a2e', color: '#fff' }}>Gestão Interna</option>
            <option value="outro" style={{ background: '#1a1a2e', color: '#fff' }}>Outro</option>
          </select>
        </div>
      </div>
    );
  }

  // STEP 4: Tipo
  function renderStep4() {
    const tipos = formData.area_atuacao === 'atendimento' ? [
      { value: 'vendas', label: 'Vendas', desc: 'Foco em vender produtos/serviços' },
      { value: 'suporte_tecnico', label: 'Suporte Técnico', desc: 'Resolver problemas técnicos' },
      { value: 'suporte_geral', label: 'Suporte Geral', desc: 'Atender dúvidas gerais' }
    ] : [
      { value: 'treinamento', label: 'Treinamento', desc: 'Treinar equipe' },
      { value: 'analise', label: 'Análise de Dados', desc: 'Analisar dados e gerar relatórios' },
      { value: 'especialista', label: 'Especialista', desc: 'Especialização em área específica' }
    ];

    return (
      <div>
        <h3 style={{ marginBottom: '1.5rem', fontSize: '1.5rem' }}>🎯 Tipo do Agente</h3>
        
        <div style={{ display: 'grid', gap: '1rem' }}>
          {tipos.map(tipo => (
            <div
              key={tipo.value}
              onClick={() => updateFormData('tipo', tipo.value)}
              style={{
                padding: '1.5rem',
                background: formData.tipo === tipo.value ? 'rgba(180, 59, 234, 0.2)' : 'rgba(255,255,255,0.05)',
                border: `2px solid ${formData.tipo === tipo.value ? 'rgba(180, 59, 234, 0.5)' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{tipo.label}</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.7 }}>{tipo.desc}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // STEP 5: Temperatura e Função
  function renderStep5() {
    return (
      <div>
        <h3 style={{ marginBottom: '1.5rem', fontSize: '1.5rem' }}>⚙️ Configurações</h3>
        
        <div style={{ marginBottom: '2rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
            Temperatura: {formData.temperatura}
          </label>
          <input
            type="range"
            min="0.1"
            max="1.0"
            step="0.1"
            value={formData.temperatura}
            onChange={(e) => updateFormData('temperatura', parseFloat(e.target.value))}
            style={{ width: '100%' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', opacity: 0.7, marginTop: '0.5rem' }}>
            <span>0.1 (Conservador)</span>
            <span>0.5 (Equilibrado)</span>
            <span>1.0 (Criativo)</span>
          </div>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
            Função Principal *
          </label>
          <textarea
            value={formData.funcao_principal}
            onChange={(e) => updateFormData('funcao_principal', e.target.value)}
            placeholder="Ex: Aumentar vendas, Coletar dados de clientes, Agendar reuniões..."
            rows={4}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '1rem',
              resize: 'vertical'
            }}
          />
        </div>
      </div>
    );
  }

  // STEP 6: Base de Conhecimento
  function renderStep6() {
    const handleAddUrl = async () => {
      if (!urlInput.trim()) {
        setError('Digite uma URL válida');
        return;
      }

      try {
        setUploadingUrl(true);
        setError(null);

        console.log('[AgentCreator] Enviando URL:', urlInput);

        const response = await fetch(`${BACKEND_NODE_URL}/api/agents/knowledge/add-url`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            agentId: formData.agente_id,
            companyId: companyId,
            url: urlInput.trim(),
            userId: userId
          })
        });

        const data = await response.json();
        console.log('[AgentCreator] Resposta do servidor:', data);

        if (!data.success) {
          throw new Error(data.error || 'Erro ao adicionar URL');
        }

        // Adicionar à lista de base de conhecimento
        updateFormData('base_conhecimento', [
          ...formData.base_conhecimento,
          { type: 'url', name: urlInput, id: data.data.id }
        ]);

        setUrlInput('');
        setShowUrlInput(false);
        alert('✅ URL adicionada com sucesso!');

      } catch (err) {
        setError(err.message);
      } finally {
        setUploadingUrl(false);
      }
    };

    const handleFileUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        setUploadingFile(true);
        setError(null);

        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('agentId', formData.agente_id);
        formDataUpload.append('companyId', companyId);
        formDataUpload.append('userId', userId || '');

        const response = await fetch(`${BACKEND_NODE_URL}/api/agents/knowledge/upload-file`, {
          method: 'POST',
          body: formDataUpload
        });

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.error || 'Erro ao fazer upload');
        }

        // Adicionar à lista de base de conhecimento
        updateFormData('base_conhecimento', [
          ...formData.base_conhecimento,
          { type: 'file', name: file.name, id: data.data.id }
        ]);

        alert('✅ Arquivo adicionado com sucesso!');

      } catch (err) {
        setError(err.message);
      } finally {
        setUploadingFile(false);
        e.target.value = ''; // Limpar input
      }
    };

    return (
      <div>
        <h3 style={{ marginBottom: '0.5rem', fontSize: '1.5rem' }}>📚 Base de Conhecimento</h3>
        <p style={{ opacity: 0.7, marginBottom: '2rem', fontSize: '0.95rem' }}>
          Adicione documentos e URLs para melhorar as respostas do agente (opcional)
        </p>

        {/* Botões de ação */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
          <label style={{
            flex: 1,
            padding: '1rem',
            background: 'rgba(59, 130, 246, 0.1)',
            border: '2px dashed rgba(59, 130, 246, 0.3)',
            borderRadius: '8px',
            cursor: uploadingFile ? 'not-allowed' : 'pointer',
            textAlign: 'center',
            opacity: uploadingFile ? 0.5 : 1
          }}>
            <input
              type="file"
              onChange={handleFileUpload}
              disabled={uploadingFile || !formData.agente_id}
              accept=".pdf,.txt,.doc,.docx,.ppt,.pptx"
              style={{ display: 'none' }}
            />
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📄</div>
            <div style={{ fontWeight: 500 }}>
              {uploadingFile ? 'Processando...' : 'Upload de Arquivo'}
            </div>
            <div style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.25rem' }}>
              PDF, TXT, DOC, DOCX, PPT, PPTX
            </div>
          </label>

          <button
            onClick={() => setShowUrlInput(!showUrlInput)}
            disabled={!formData.agente_id}
            style={{
              flex: 1,
              padding: '1rem',
              background: 'rgba(139, 92, 246, 0.1)',
              border: '2px dashed rgba(139, 92, 246, 0.3)',
              borderRadius: '8px',
              cursor: formData.agente_id ? 'pointer' : 'not-allowed',
              textAlign: 'center',
              color: '#fff',
              opacity: formData.agente_id ? 1 : 0.5
            }}
          >
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🌐</div>
            <div style={{ fontWeight: 500 }}>Adicionar URL</div>
            <div style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.25rem' }}>
              Sites, artigos, páginas web
            </div>
          </button>
        </div>

        {/* Input de URL */}
        {showUrlInput && (
          <div style={{
            padding: '1.5rem',
            background: 'rgba(139, 92, 246, 0.1)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '8px',
            marginBottom: '2rem'
          }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              URL da página
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="www.exemplo.com/artigo ou https://exemplo.com"
                disabled={uploadingUrl}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              />
              <button
                onClick={handleAddUrl}
                disabled={uploadingUrl || !urlInput.trim()}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: uploadingUrl ? 'rgba(139, 92, 246, 0.3)' : 'rgba(139, 92, 246, 0.5)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  cursor: uploadingUrl || !urlInput.trim() ? 'not-allowed' : 'pointer',
                  fontWeight: 500
                }}
              >
                {uploadingUrl ? 'Processando...' : 'Adicionar'}
              </button>
            </div>
          </div>
        )}

        {/* Lista de documentos adicionados */}
        {formData.base_conhecimento.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <h4 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>📋 Documentos Adicionados</h4>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {formData.base_conhecimento.map((doc, index) => (
                <div
                  key={index}
                  style={{
                    padding: '1rem',
                    background: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}
                >
                  <span style={{ fontSize: '1.5rem' }}>
                    {doc.type === 'url' ? '🌐' : '📄'}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>{doc.name}</div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>
                      {doc.type === 'url' ? 'URL' : 'Arquivo'}
                    </div>
                  </div>
                  <span style={{ color: '#86efac' }}>✓</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Informação */}
        <div style={{
          padding: '1rem',
          background: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '8px'
        }}>
          <p style={{ margin: 0, color: '#93c5fd', fontSize: '0.9rem' }}>
            💡 Os documentos serão processados com IA e usados para melhorar as respostas do agente. Você pode adicionar mais documentos depois.
          </p>
        </div>
      </div>
    );
  }

  // STEP 7: Catálogos
  function renderStep7() {
    return (
      <div>
        <h3 style={{ marginBottom: '0.5rem', fontSize: '1.5rem' }}>📦 Catálogos de Produtos</h3>
        <p style={{ marginBottom: '2rem', opacity: 0.7, fontSize: '0.95rem' }}>
          Selecione as categorias de produtos que o agente poderá consultar
        </p>
        
        {formData.catalogos_disponiveis.length === 0 ? (
          <div style={{
            padding: '2rem',
            background: 'rgba(255, 193, 7, 0.1)',
            border: '1px solid rgba(255, 193, 7, 0.3)',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p style={{ margin: 0, color: '#ffd54f' }}>⚠️ Nenhuma categoria encontrada</p>
            <p style={{ fontSize: '0.9rem', opacity: 0.7, marginTop: '0.5rem', margin: '0.5rem 0 0 0' }}>
              Crie categorias de produtos em: Menu → Produtos
            </p>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gap: '1rem', marginBottom: '1.5rem' }}>
              {formData.catalogos_disponiveis.map(cat => {
                const isSelected = formData.catalogos_ids.includes(cat.id);
                return (
                  <label
                    key={cat.id}
                    style={{
                      display: 'flex',
                      gap: '1rem',
                      padding: '1.5rem',
                      background: isSelected ? 'rgba(139, 92, 246, 0.15)' : 'rgba(255,255,255,0.05)',
                      border: `2px solid ${isSelected ? 'rgba(139, 92, 246, 0.5)' : 'rgba(255,255,255,0.1)'}`,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      ':hover': {
                        background: 'rgba(255,255,255,0.08)'
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        if (e.target.checked) {
                          updateFormData('catalogos_ids', [...formData.catalogos_ids, cat.id]);
                        } else {
                          updateFormData('catalogos_ids', formData.catalogos_ids.filter(id => id !== cat.id));
                        }
                      }}
                      style={{ 
                        width: '22px', 
                        height: '22px',
                        marginTop: '2px',
                        cursor: 'pointer',
                        accentColor: '#8b5cf6'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontWeight: 600, 
                        fontSize: '1.1rem',
                        marginBottom: '0.5rem',
                        color: isSelected ? '#c4b5fd' : '#fff'
                      }}>
                        📂 {cat.nome}
                      </div>
                      
                      {cat.descricao && (
                        <div style={{ 
                          fontSize: '0.9rem', 
                          opacity: 0.8,
                          marginBottom: '0.75rem',
                          lineHeight: '1.4'
                        }}>
                          {cat.descricao}
                        </div>
                      )}
                      
                      <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.85rem',
                        opacity: 0.7
                      }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          background: 'rgba(59, 130, 246, 0.2)',
                          borderRadius: '12px',
                          color: '#93c5fd'
                        }}>
                          {cat.total_produtos || 0} produto{cat.total_produtos !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            {formData.catalogos_ids.length > 0 && (
              <div style={{
                padding: '1rem',
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '8px'
              }}>
                <p style={{ margin: 0, color: '#86efac', fontSize: '0.9rem' }}>
                  ✓ {formData.catalogos_ids.length} categoria{formData.catalogos_ids.length !== 1 ? 's' : ''} selecionada{formData.catalogos_ids.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // STEP 8: Formulário (centralizado no Projeto CRM)
  function renderStep8() {
    return (
      <div>
        <h3 style={{ marginBottom: '0.5rem', fontSize: '1.5rem' }}>📋 Formulário de Coleta</h3>
        <p style={{ opacity: 0.7, marginBottom: '2rem', fontSize: '0.95rem' }}>
          O formulário de coleta de dados é configurado diretamente no Projeto CRM
        </p>

        <div style={{
          padding: '2rem',
          background: 'rgba(68, 225, 255, 0.1)',
          border: '1px solid rgba(68, 225, 255, 0.3)',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
          <h4 style={{ margin: '0 0 1rem 0', color: '#44e1ff' }}>Formulário centralizado no Projeto CRM</h4>
          <p style={{ margin: '0 0 1.5rem 0', opacity: 0.8, fontSize: '0.95rem', lineHeight: '1.6' }}>
            Os campos do formulário de coleta de dados agora são configurados diretamente 
            na lista <strong>"Equipe de Vendas"</strong> do Projeto CRM vinculado a este agente.
          </p>
          <p style={{ margin: 0, opacity: 0.6, fontSize: '0.9rem' }}>
            Acesse o Projeto CRM &gt; Lista "Equipe de Vendas" &gt; Configurações para gerenciar os campos.
          </p>
        </div>

        {formData.projeto_id && (
          <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '8px'
          }}>
            <p style={{ margin: 0, color: '#86efac', fontSize: '0.95rem' }}>
              ✅ Projeto CRM vinculado. Os campos do formulário serão lidos automaticamente do projeto.
            </p>
          </div>
        )}

        {!formData.projeto_id && (
          <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: 'rgba(251, 191, 36, 0.1)',
            border: '1px solid rgba(251, 191, 36, 0.3)',
            borderRadius: '8px'
          }}>
            <p style={{ margin: 0, color: '#fbbf24', fontSize: '0.95rem' }}>
              ⚠️ Nenhum projeto CRM vinculado. Vincule um projeto na Etapa 2 para usar o formulário.
            </p>
          </div>
        )}
      </div>
    );
  }

  // STEP 9: Resumo
  function renderStep9() {
    // Buscar nomes das seleções
    const conexaoSelecionada = formData.conexoes.find(c => c.id === formData.conexao_id);
    const projetoSelecionado = formData.projetos.find(p => p.id === formData.projeto_id);
    const catalogosSelecionados = formData.catalogos_disponiveis.filter(cat => 
      formData.catalogos_ids.includes(cat.id)
    );

    return (
      <div>
        <h3 style={{ marginBottom: '0.5rem', fontSize: '1.5rem' }}>✅ Resumo Final</h3>
        <p style={{ marginBottom: '2rem', opacity: 0.7, fontSize: '0.95rem' }}>
          Revise as configurações antes de gerar o prompt
        </p>
        
        <div style={{ background: 'rgba(255,255,255,0.05)', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem' }}>
          <h4 style={{ marginBottom: '1rem', color: '#a78bfa' }}>🤖 Agente</h4>
          <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.95rem' }}>
            <div><strong>Nome:</strong> {formData.nome}</div>
            <div><strong>Área:</strong> {formData.area_atuacao}</div>
            <div><strong>Tipo:</strong> {formData.tipo}</div>
            <div><strong>Temperatura:</strong> {formData.temperatura}</div>
            <div><strong>Função:</strong> {formData.funcao_principal}</div>
          </div>
        </div>

        {conexaoSelecionada && (
          <div style={{ background: 'rgba(255,255,255,0.05)', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem' }}>
            <h4 style={{ marginBottom: '1rem', color: '#a78bfa' }}>📱 Conexão WhatsApp</h4>
            <div style={{ fontSize: '0.95rem' }}>
              <strong>{conexaoSelecionada.name || conexaoSelecionada.instance_name}</strong>
              {conexaoSelecionada.phone_number && (
                <div style={{ opacity: 0.7, marginTop: '0.25rem' }}>
                  {conexaoSelecionada.phone_number}
                </div>
              )}
            </div>
          </div>
        )}

        {projetoSelecionado && (
          <div style={{ background: 'rgba(255,255,255,0.05)', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem' }}>
            <h4 style={{ marginBottom: '1rem', color: '#a78bfa' }}>📊 Projeto CRM</h4>
            <div style={{ fontSize: '0.95rem' }}>
              <strong>{projetoSelecionado.name}</strong>
              {projetoSelecionado.description && (
                <div style={{ opacity: 0.7, marginTop: '0.25rem' }}>
                  {projetoSelecionado.description}
                </div>
              )}
            </div>
          </div>
        )}

        {formData.base_conhecimento.length > 0 && (
          <div style={{ background: 'rgba(255,255,255,0.05)', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem' }}>
            <h4 style={{ marginBottom: '1rem', color: '#a78bfa' }}>📚 Base de Conhecimento</h4>
            <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.9rem' }}>
              {formData.base_conhecimento.map((doc, index) => (
                <div key={index}>
                  • {doc.name} <span style={{ opacity: 0.6 }}>({doc.type})</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {catalogosSelecionados.length > 0 && (
          <div style={{ background: 'rgba(255,255,255,0.05)', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem' }}>
            <h4 style={{ marginBottom: '1rem', color: '#a78bfa' }}>📦 Catálogos</h4>
            <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.9rem' }}>
              {catalogosSelecionados.map((cat, index) => (
                <div key={index}>
                  • <strong>{cat.nome}</strong> - {cat.total_produtos} produto{cat.total_produtos !== 1 ? 's' : ''}
                </div>
              ))}
            </div>
          </div>
        )}

        {gerandoPrompt ? (
          <div style={{
            padding: '2rem',
            background: 'rgba(139, 92, 246, 0.1)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '8px',
            marginTop: '2rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🤖</div>
            <p style={{ margin: 0, color: '#c4b5fd', fontSize: '1.1rem', marginBottom: '0.5rem' }}>
              Gerando instruções com IA...
            </p>
            <p style={{ margin: 0, opacity: 0.7, fontSize: '0.9rem' }}>
              Isso pode levar alguns segundos
            </p>
          </div>
        ) : (
          <div style={{
            padding: '1rem',
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '8px',
            marginTop: '2rem'
          }}>
            <p style={{ margin: 0, color: '#86efac', fontSize: '0.95rem' }}>
              ✨ Ao clicar em "Avançar", o prompt do agente será gerado automaticamente com IA baseado nestas configurações.
            </p>
          </div>
        )}
      </div>
    );
  }

  // STEP 10: Prompt Gerado (Editável)
  function renderStep10() {
    return (
      <div>
        <h3 style={{ marginBottom: '0.5rem', fontSize: '1.5rem' }}>✨ Prompt do Agente</h3>
        <p style={{ marginBottom: '2rem', opacity: 0.7, fontSize: '0.95rem' }}>
          Revise e edite as instruções geradas automaticamente pela IA
        </p>

        {gerandoPrompt ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            background: 'rgba(139, 92, 246, 0.1)',
            borderRadius: '12px'
          }}>
            <div className="spinner" style={{ margin: '0 auto 1rem auto', width: '50px', height: '50px' }}></div>
            <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem', fontWeight: 600 }}>🤖 Gerando instruções com IA...</p>
            <p style={{ opacity: 0.7, fontSize: '0.9rem', marginBottom: '0.5rem' }}>Isso pode levar até 2 minutos</p>
            <p style={{ opacity: 0.5, fontSize: '0.85rem' }}>Aguarde, não feche esta janela</p>
          </div>
        ) : (
          <>
            {/* Personalidade */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                🎭 Personalidade
              </label>
              <textarea
                value={typeof promptGerado.personalidade === 'object' 
                  ? JSON.stringify(promptGerado.personalidade, null, 2) 
                  : (promptGerado.personalidade || '')}
                onChange={(e) => setPromptGerado({ ...promptGerado, personalidade: e.target.value })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.95rem',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Contexto */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                📋 Contexto
              </label>
              <textarea
                value={typeof promptGerado.contexto === 'object' 
                  ? JSON.stringify(promptGerado.contexto, null, 2) 
                  : (promptGerado.contexto || '')}
                onChange={(e) => setPromptGerado({ ...promptGerado, contexto: e.target.value })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.95rem',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Direcionamento */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                🎯 Direcionamento
              </label>
              <textarea
                value={typeof promptGerado.direcionamento === 'object' 
                  ? JSON.stringify(promptGerado.direcionamento, null, 2) 
                  : (promptGerado.direcionamento || '')}
                onChange={(e) => setPromptGerado({ ...promptGerado, direcionamento: e.target.value })}
                rows={5}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.95rem',
                  resize: 'vertical',
                  fontFamily: 'monospace'
                }}
              />
            </div>

            {/* Ferramentas */}
            {promptGerado.ferramentas && (
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  🔧 Ferramentas
                </label>
                <textarea
                  value={typeof promptGerado.ferramentas === 'object' 
                    ? JSON.stringify(promptGerado.ferramentas, null, 2) 
                    : (promptGerado.ferramentas || '')}
                  onChange={(e) => setPromptGerado({ ...promptGerado, ferramentas: e.target.value })}
                  rows={5}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.95rem',
                    resize: 'vertical',
                    fontFamily: 'monospace'
                  }}
                />
              </div>
            )}

            {/* Restrições */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                🚫 Restrições
              </label>
              <textarea
                value={typeof promptGerado.restricoes === 'object' 
                  ? JSON.stringify(promptGerado.restricoes, null, 2) 
                  : (promptGerado.restricoes || '')}
                onChange={(e) => setPromptGerado({ ...promptGerado, restricoes: e.target.value })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.95rem',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Objetivos */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                🎯 Objetivos
              </label>
              <textarea
                value={typeof promptGerado.objetivos === 'object' 
                  ? JSON.stringify(promptGerado.objetivos, null, 2) 
                  : (promptGerado.objetivos || '')}
                onChange={(e) => setPromptGerado({ ...promptGerado, objetivos: e.target.value })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.95rem',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Informações Gerais */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                ℹ️ Informações Gerais
              </label>
              <textarea
                value={typeof promptGerado.informacoesGerais === 'object' 
                  ? JSON.stringify(promptGerado.informacoesGerais, null, 2) 
                  : (promptGerado.informacoesGerais || '')}
                onChange={(e) => setPromptGerado({ ...promptGerado, informacoesGerais: e.target.value })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.95rem',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{
              padding: '1rem',
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              borderRadius: '8px'
            }}>
              <p style={{ margin: 0, color: '#86efac', fontSize: '0.95rem' }}>
                ✅ Ao clicar em "Finalizar", o agente será criado com estas instruções.
              </p>
            </div>
          </>
        )}
      </div>
    );
  }
};

export default AgentCreatorStepModal;